import { forwardRef, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { CheckIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import { classNames } from 'support/helpers/generic/generic';

import { Button } from '../Button';

type InlineInputProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'onKeyDown'> & {
  onSave: () => Promise<void> | void;
  error?: FieldError;
};

const InlineInputWithRef = (
  { onSave, value, defaultValue, error, ...inputProps }: InlineInputProps,
  ref: React.Ref<HTMLInputElement>,
) => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const displayValue = value ?? defaultValue;

  const save = async () => {
    try {
      setLoading(true);
      await onSave();
      setEditing(false);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !error && !loading) {
      e.preventDefault();
      save();
    }
  };

  const content = editing ? (
    <input
      {...inputProps}
      ref={ref}
      value={value}
      defaultValue={defaultValue}
      onKeyDown={handleKeyDown}
      className={classNames(
        'max-w-[105px] border-0 border-b bg-transparent pb-[2px] pl-1 pr-0 pt-0 text-right text-xs outline-none focus:ring-0',
        error
          ? 'border-red-500 text-red-700 focus:border-red-500'
          : 'border-gray-300 text-gray-900 focus:border-procuros-green-400',
      )}
    />
  ) : (
    <Button
      analyticsId="inline_input_edit"
      variant="minimal"
      size="extra-small"
      onClick={() => setEditing(true)}
      className="!p-0 text-xs text-gray-900"
    >
      {displayValue}
    </Button>
  );

  const iconButton = editing ? (
    <Button
      iconOnly
      size="extra-small"
      variant="minimal"
      RightIcon={CheckIcon}
      loading={loading}
      analyticsId="inline_input_save"
      onClick={save}
      disabled={Boolean(error) || loading}
      className="size-4 !p-1 !pr-0 text-gray-400 hover:text-gray-600 disabled:opacity-50 [&>svg]:size-4"
    />
  ) : (
    <Button
      iconOnly
      variant="minimal"
      RightIcon={PencilSquareIcon}
      loading={loading}
      analyticsId="inline_input_edit"
      onClick={() => setEditing(true)}
      className="size-4 !p-1 !pr-0 text-gray-400 hover:text-gray-600 [&>svg]:size-4"
    />
  );

  return (
    <div className="inline-flex items-start gap-1">
      {content}
      {iconButton}
    </div>
  );
};

export const InlineInput = forwardRef(InlineInputWithRef);
